import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { classificationService } from '../../services/classificationService';
import serviceClassificationService from '../../services/serviceClassificationService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const countHierarchy = (hierarchy) => {
  const cats = hierarchy?.length || 0;
  const subs = (hierarchy || []).reduce((sum, cat) => sum + (cat.subcategories?.length || 0), 0);
  const fams = (hierarchy || []).reduce(
    (sum, cat) =>
      sum + (cat.subcategories || []).reduce((subSum, sub) => subSum + (sub.families?.length || 0), 0),
    0
  );
  return { cats, subs, fams };
};

const ClassificationIndex = () => {
  const { servicesClassificationEnabled } = useModuleSettings();
  const [productHierarchy, setProductHierarchy] = useState([]);
  const [serviceHierarchy, setServiceHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [productsRes, servicesRes] = await Promise.all([
          classificationService.getHierarchy({ active_only: false }),
          servicesClassificationEnabled
            ? serviceClassificationService.getHierarchy({ active_only: false })
            : Promise.resolve({ success: true, data: [] }),
        ]);
        if (cancelled) return;
        if (productsRes.success) setProductHierarchy(productsRes.data || []);
        if (servicesRes.success) setServiceHierarchy(servicesRes.data || []);
      } catch {
        // mantener listas vacías
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [servicesClassificationEnabled]);

  const productCounts = countHierarchy(productHierarchy);
  const serviceCounts = countHierarchy(serviceHierarchy);

  const productMenuItems = [
    {
      title: 'Categorías',
      description: 'Gestionar categorías de productos (Nivel 1)',
      link: '/classification/categories',
      icon: '📁',
      color: 'teal',
      count: productCounts.cats,
    },
    {
      title: 'Subcategorías',
      description: 'Gestionar subcategorías de productos (Nivel 2)',
      link: '/classification/subcategories',
      icon: '📂',
      color: 'purple',
      count: productCounts.subs,
    },
    {
      title: 'Familias',
      description: 'Gestionar familias de productos (Nivel 3)',
      link: '/classification/families',
      icon: '📋',
      color: 'indigo',
      count: productCounts.fams,
    },
    {
      title: 'Vista Jerárquica',
      description: 'Ver clasificación completa de productos',
      link: '/classification/hierarchy',
      icon: '🌳',
      color: 'green',
      count: null,
    },
  ];

  const serviceMenuItems = [
    {
      title: 'Categorías',
      description: 'Gestionar categorías de servicios (Nivel 1)',
      link: '/classification/services/categories',
      icon: '📁',
      color: 'blue',
      count: serviceCounts.cats,
    },
    {
      title: 'Subcategorías',
      description: 'Gestionar subcategorías de servicios (Nivel 2)',
      link: '/classification/services/subcategories',
      icon: '📂',
      color: 'cyan',
      count: serviceCounts.subs,
    },
    {
      title: 'Familias',
      description: 'Gestionar familias de servicios (Nivel 3)',
      link: '/classification/services/families',
      icon: '📋',
      color: 'sky',
      count: serviceCounts.fams,
    },
    {
      title: 'Vista Jerárquica',
      description: 'Ver clasificación completa de servicios',
      link: '/classification/services/hierarchy',
      icon: '🌳',
      color: 'green',
      count: null,
    },
  ];

  const renderCards = (items) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {items.map((item) => (
        <Link
          key={item.link}
          to={item.link}
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="text-4xl bg-gray-100 p-3 rounded-lg">{item.icon}</div>
            {item.count !== null && (
              <span className="px-3 py-1 text-sm font-bold rounded-full bg-gray-100 text-gray-800">
                {loading ? '…' : item.count}
              </span>
            )}
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
          <p className="text-gray-600 text-sm">{item.description}</p>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Clasificación</h1>
          <p className="text-gray-600">Jerarquía de 3 niveles para productos y servicios</p>
        </div>

        <nav className="text-sm text-gray-600 mb-6">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Clasificación</span>
        </nav>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">Productos</h2>
          <p className="text-sm text-gray-500 mb-4">Asigne familias al crear o editar productos.</p>
          {renderCards(productMenuItems)}
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-1">Servicios</h2>
          {servicesClassificationEnabled ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Defina la clasificación de servicios y asígnela al crear o editar cada servicio.
                Los que no tengan familia aparecen como pendientes en el listado de servicios.
              </p>
              {renderCards(serviceMenuItems)}
            </>
          ) : (
            <div className="bg-white rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-600">
              La clasificación de servicios no está habilitada. Actívela en{' '}
              <Link to="/admin/general-settings" className="text-teal-600 hover:text-teal-800 font-medium">
                Administración → Configuración general
              </Link>
              .
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ClassificationIndex;
